import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

interface TtsResponse {
  success: boolean;
  audioBase64?: string;
  contentType?: string;
  useBrowserTTS?: boolean;
  error?: string;
}

/**
 * Speaks assistant text via the ttsSpeak Cloud Function (ElevenLabs), and
 * falls back to the browser's built-in speech synthesis if ElevenLabs isn't
 * configured or the call fails. The ElevenLabs key lives only in the function.
 */
class TtsService {
  private audio: HTMLAudioElement | null = null;

  async speak(text: string, voiceId?: string): Promise<void> {
    this.stop();
    const clean = (text || '').trim();
    if (!clean) return;

    const auth = getAuth();
    if (!auth.currentUser) {
      this.browserSpeak(clean);
      return;
    }

    try {
      const callable = httpsCallable<{ text: string; voiceId?: string }, TtsResponse>(
        getFunctions(),
        'ttsSpeak',
        { timeout: 30000 }
      );
      const res = await callable({ text: clean, voiceId });
      const data = res.data;
      if (data?.audioBase64) {
        const audio = new Audio(`data:${data.contentType || 'audio/mpeg'};base64,${data.audioBase64}`);
        this.audio = audio;
        audio.onended = () => {
          this.audio = null;
        };
        await audio.play();
        return;
      }
      // useBrowserTTS or empty payload → browser fallback
      this.browserSpeak(clean);
    } catch (e) {
      console.error('TTS error, falling back to browser speech:', e);
      this.browserSpeak(clean);
    }
  }

  private browserSpeak(text: string): void {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try {
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(text);
        window.speechSynthesis.speak(utter);
      } catch {
        /* noop */
      }
    }
  }

  stop(): void {
    if (this.audio) {
      try {
        this.audio.pause();
      } catch {
        /* noop */
      }
      this.audio = null;
    }
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try {
        window.speechSynthesis.cancel();
      } catch {
        /* noop */
      }
    }
  }
}

export const ttsService = new TtsService();
